import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Stack,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateBooking } from "../../api/bookings";
import type { Booking } from "../../api/bookings";
import type { PendingSyncRetry } from "../../store/bookingsSlice";

interface EditBookingModalProps {
  booking: Booking | null;
  onClose: () => void;
  onSyncFailed?: (payload: PendingSyncRetry) => void;
}

export const EditBookingModal: React.FC<EditBookingModalProps> = ({
  booking,
  onClose,
  onSyncFailed,
}) => {
  const queryClient = useQueryClient();
  const [serviceName, setServiceName] = useState(booking?.service_name ?? "");
  const [notes, setNotes] = useState(booking?.notes ?? "");

  const mutation = useMutation({
    mutationFn: () =>
      updateBooking(booking!.booking_id, {
        service_name: serviceName,
        notes,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      if (!result.calendar_synced && result.calendar_sync_error && onSyncFailed && booking) {
        onSyncFailed({
          bookingId: booking.booking_id,
          action: "update_title",
          newTitle: serviceName,
        });
      }
      onClose();
    },
  });

  if (!booking) return null;

  return (
    <Dialog open={Boolean(booking)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Booking</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Service Name"
            fullWidth
            size="small"
            value={serviceName}
            onChange={(e) => setServiceName(e.target.value)}
          />
          <TextField
            label="Notes"
            fullWidth
            size="small"
            multiline
            minRows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
        {mutation.isError && (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            Failed to save changes. Please try again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || !serviceName.trim()}
          startIcon={
            mutation.isPending ? <CircularProgress size={16} /> : undefined
          }
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
